'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  registerPostHog,
  getConsent,
  capturePageView,
  e2eAnalyticsOverride,
  isAnalyticsEnabledByEnv,
  trackLandingView,
  applyAnalyticsOptIn,
  getPostHog,
  ANALYTICS_CONSENT_CHANGED_EVENT,
  ANALYTICS_POSTHOG_READY_EVENT,
  type AnalyticsConsentChangedDetail,
} from '@/lib/analytics';
import {
  currentAuthSecretStripEpoch,
  isSessionReplayExcludedPath,
  scrubAuthSecretsFromCapture,
  subscribeAuthSecretStrip,
  syncSessionReplayForPath,
} from '@/lib/analytics/authSecretUrl';
// CJS module shared with next.config.js (WEB-562/WEB-815). allowJs + bundler resolution.
import { DEFAULT_CLIENT_ENV } from '@/lib/nextConfigClientEnv.js';

/**
 * Initializes PostHog when key is set, env allows, AND the user has granted
 * consent (FRE-809 / HG-10). Lazy-loads posthog-js to avoid blocking render
 * (<100ms requirement) and to honor the Privacy Policy promise:
 *
 *   "Non-essential analytics + PostHog gated behind consent."
 *
 * Behavior:
 * - No consent decision yet → no `posthog.init()`, no `/decide` request,
 *   no PostHog cookies set.
 * - Consent denied → no init.
 * - Consent granted (now or later via the cookie banner / Settings → Privacy)
 *   → init once. A `loci:analytics-consent-changed` CustomEvent triggers
 *   late init when the user accepts after the page has loaded.
 *
 * Essential cookies (auth `accessToken` / `refreshToken` / `loci_session`,
 * CSRF) are server-set httpOnly cookies. They are completely outside this
 * provider's surface area, so declining cannot break login.
 */
function usePostHogInit(): void {
  const initStarted = useRef(false);

  useEffect(() => {
    if (!isAnalyticsEnabledByEnv()) return;

    const override = e2eAnalyticsOverride();
    const key = override?.key ?? process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host =
      override?.host ??
      process.env.NEXT_PUBLIC_POSTHOG_HOST ??
      DEFAULT_CLIENT_ENV.NEXT_PUBLIC_POSTHOG_HOST;
    const uiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? 'https://us.posthog.com';
    if (!key || key.length < 10) return;

    const initPostHog = (): void => {
      if (initStarted.current) return;
      initStarted.current = true;

      void import('posthog-js').then(({ default: posthog }) => {
        // WEB-1204: read before init. opt_in_capturing (below) calls
        // startIfEnabledOrStop, which loads the recorder when remote config
        // has replay on. A stop after that load has started cannot cancel it:
        // _onScriptLoaded starts rrweb without re-reading the disable flag.
        const replayExcluded = isSessionReplayExcludedPath(window.location.pathname);
        posthog.init(key, {
          api_host: host,
          // Reverse-proxied through p.loci.city (first-party domain, avoids ad
          // blockers); ui_host keeps toolbar/session-replay links pointed at
          // the real PostHog app instead of the proxy. Configurable so a
          // region migration or a preview env pointed at a different
          // PostHog org doesn't require a code change.
          ui_host: uiHost,
          capture_pageview: false, // we capture manually on pathname change for SPA
          capture_pageleave: true,
          persistence: 'localStorage+cookie',
          autocapture: true,
          // Error tracking: autocapture unhandled exceptions + unhandled promise
          // rejections as `$exception` events so the already-configured
          // error_tracking signal sources can actually fire. Errors that a React
          // error boundary swallows never reach this handler — those are
          // forwarded manually via `captureException()` (see app/error.tsx).
          // Console-error capture is intentionally left off (its default) to
          // avoid turning handled `console.error` logging into an issue firehose.
          capture_exceptions: true,
          respect_dnt: true,
          person_profiles: 'identified_only',
          // WEB-1204: autocapture and $pageleave stamp $current_url from
          // window.location after this init. The token pages also strip the
          // address bar first; this removes token/code/t if one is still
          // present on $current_url, $referrer, or $initial_current_url.
          before_send: scrubAuthSecretsFromCapture,
          // Same race: the flag has to be on before init's extensions and
          // before opt-in. Absent on every other path, so replay stays a
          // remote-config decision there.
          ...(replayExcluded ? { disable_session_recording: true } : {}),
        });

        // Before opt-in, so startIfEnabledOrStop sees the disable flag and
        // takes the stop branch. Also records that leaving this path should
        // resume. PageViewTracker repeats the sync on later navigations.
        syncSessionReplayForPath(posthog, window.location.pathname);

        // We only ever reach init() after `getConsent() === 'granted'`, so the
        // post-init opt-in ritual (opt in + baseline person props + any
        // retained identify — FRE-1261) is the single right call.
        //
        // It runs BEFORE registerPostHog (WEB-1182): registering replays the
        // pre-init capture buffer and announces readiness (which can capture
        // the current page), and posthog-js's capture() is a no-op while the
        // visitor is opted out. An opt-out persisted by an earlier decline
        // stays in force until opt_in_capturing(), so replaying first would
        // silently drop the very entry pageview this buffer exists to save.
        applyAnalyticsOptIn(posthog);
        registerPostHog(posthog);
        // The npm bundle does not attach the client to `window`, which made
        // the operator console recipes (flag overrides — see
        // docs/runbooks/locus-rollout-runbook.md §7) unreachable in
        // production. Exposing it is PostHog's documented debugging pattern.
        window.posthog = posthog;
      });
    };

    // Eager init when consent has *already* been granted on a prior visit.
    // Otherwise defer until the user accepts via the banner / Settings.
    if (getConsent() === 'granted') {
      initPostHog();
    }

    const handleConsentChange = (event: Event): void => {
      const detail = (event as CustomEvent<AnalyticsConsentChangedDetail>).detail;
      if (detail?.granted) {
        initPostHog();
      }
    };

    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange);
    };
  }, []);
}

/**
 * Capture (or buffer, while PostHog is still loading) the entry events for a
 * page. Returns whether they were accepted — `false` means the consent/env gate
 * dropped them and the page still needs reporting once consent arrives.
 */
function reportPageEntry(path: string): boolean {
  const url = typeof window !== 'undefined' ? window.location.href : undefined;
  if (!capturePageView(path, url)) return false;
  if (path === '/') {
    trackLandingView();
  }
  return true;
}

/**
 * Captures page view on route change (App Router SPA navigation).
 *
 * `reportedPath` is the page whose entry events were captured *or buffered*
 * (WEB-1182). Two moments report the current page, and the ref keeps them from
 * double-counting it:
 * - the route effect — covers the already-granted load (the view is buffered
 *   by `lib/analytics` until the lazy client registers) and every navigation;
 * - PostHog becoming ready — covers a late consent grant, where the route
 *   effect ran while consent was absent and the gate dropped the view, so
 *   nothing was reported for the page the user is still on.
 */
function PageViewTracker(): null {
  const pathname = usePathname();
  const currentPath = useRef<string | null>(null);
  const reportedPath = useRef<string | null>(null);
  // Epoch already paired with a successful auth-route pageview. A failed
  // report (consent still absent) leaves it unchanged so the ready event can
  // retry. A later visit has to strip again — the epoch moves — before
  // another pageview. A held visit stays at the epoch from before this page
  // mounted.
  const consumedStripEpoch = useRef(0);

  const reportCurrentPage = useCallback((): void => {
    const path = currentPath.current;
    if (!path || path === reportedPath.current) return;
    // WEB-1204: the token page suspends on useSearchParams, so this effect
    // can run while the secret is still in the address bar. Hold the view
    // until useCapturedAuthToken strips and notifies. Do not strip here —
    // that would run before the page snapshots the token.
    if (
      isSessionReplayExcludedPath(path) &&
      currentAuthSecretStripEpoch() === consumedStripEpoch.current
    ) {
      return;
    }
    // Consume the epoch only after the view is accepted. Consent can still
    // be absent when the strip lands; marking the epoch then would drop the
    // pageview the ready event is supposed to retry.
    if (!reportPageEntry(path)) return;
    if (isSessionReplayExcludedPath(path)) {
      consumedStripEpoch.current = currentAuthSecretStripEpoch();
    }
    reportedPath.current = path;
  }, []);

  useEffect(() => {
    currentPath.current = pathname;
    const client = getPostHog();
    if (client) syncSessionReplayForPath(client, pathname);
    // Subscribe before the readiness check so a strip in this same turn
    // cannot land between the check and the listener.
    const unsubscribe = subscribeAuthSecretStrip(reportCurrentPage);
    reportCurrentPage();
    return unsubscribe;
  }, [pathname, reportCurrentPage]);

  useEffect(() => {
    window.addEventListener(ANALYTICS_POSTHOG_READY_EVENT, reportCurrentPage);
    return () => {
      window.removeEventListener(ANALYTICS_POSTHOG_READY_EVENT, reportCurrentPage);
    };
  }, [reportCurrentPage]);

  return null;
}

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Client-side analytics provider. Initializes PostHog when enabled and registers
 * the client for lib/analytics. Tracks page views on route change.
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps): React.ReactElement {
  usePostHogInit();

  return (
    <>
      <PageViewTracker />
      {children}
    </>
  );
}

export { getConsent, setConsent } from '@/lib/analytics';
